import React, { useMemo, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { launchImageLibrary } from 'react-native-image-picker';
import { colors, radius, spacing, typography } from '../../config/theme';
import { Chip, ErrorBanner, Field, PrimaryButton, TextButton } from '../ui';
import { PHASE, taskStateLabel } from './shiftLabels';

const MAX_PHOTOS = 8;

// Captures whatever a task requires (comment / amount / photo) before completing
// it. Photos are picked here and passed up as descriptors; the server uploads
// them to Telegram.
export default function TaskSheet({ task, onClose, onComplete }) {
  const req = useMemo(() => task?.requirements || {}, [task]);
  const [comment, setComment] = useState('');
  const [amount, setAmount] = useState('');
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const missing = useMemo(() => {
    const m = [];
    if (req.commentRequired && !comment.trim()) m.push('комментарий');
    if (req.amountRequired && !(amount.trim() && Number.isFinite(Number(amount)))) m.push('сумму');
    if (req.photoRequired && photos.length === 0) m.push('фото');
    return m;
  }, [req, comment, amount, photos]);

  if (!task) return null;

  const addPhotos = async () => {
    try {
      const room = MAX_PHOTOS - photos.length;
      if (room <= 0) { setError(`Максимум ${MAX_PHOTOS} фото`); return; }
      // selectionLimit 0 = let the picker take as many as it allows.
      const result = await launchImageLibrary({ mediaType: 'photo', selectionLimit: room === MAX_PHOTOS ? 0 : room, includeBase64: true, maxWidth: 1280, maxHeight: 1280, quality: 0.65 });
      const picked = (result?.assets || [])
        .filter((a) => a?.base64)
        .map((a, i) => ({ id: `${Date.now()}_${i}`, name: a.fileName || `photo${i + 1}.jpg`, mimeType: a.type || 'image/jpeg', base64: a.base64, uri: a.uri || null }));
      if (picked.length) setPhotos((prev) => [...prev, ...picked].slice(0, MAX_PHOTOS));
    } catch {
      setError('Не удалось открыть камеру');
    }
  };

  const removePhoto = (id) => setPhotos((prev) => prev.filter((p) => p.id !== id));

  const submit = async () => {
    if (missing.length) { setError(`Добавьте ${missing.join(', ')}`); return; }
    setBusy(true);
    setError(null);
    try {
      await onComplete({
        draft: {
          comment: req.commentRequired ? comment.trim() : undefined,
          amount: req.amountRequired ? Number(amount) : undefined,
          photoCount: photos.length,
        },
        photos: photos.map(({ name, mimeType, base64 }) => ({ name, mimeType, base64 })),
      });
      onClose();
    } catch (e) {
      setError(e?.code ? `Отклонено сервером: ${e.code}` : 'Не удалось отправить');
    } finally {
      setBusy(false);
    }
  };

  const wantsPhoto = req.photoRequired || photos.length > 0;

  return (
    <Modal visible animationType="slide" transparent onRequestClose={onClose}>
      <View style={styles.wrap}>
        <View style={styles.sheet}>
          <View style={styles.head}>
            <View style={styles.grow}>
              <Text style={styles.title}>{task.title}</Text>
              <Text style={styles.meta}>{PHASE[task.phase] || task.phase} · {taskStateLabel(task.status)}</Text>
            </View>
            <TextButton title="Закрыть" kind="muted" onPress={onClose} />
          </View>

          <ScrollView contentContainerStyle={styles.body} keyboardShouldPersistTaps="handled">
            <ErrorBanner message={error} />
            {task.description ? <Text style={styles.desc}>{task.description}</Text> : null}

            <View style={styles.chips}>
              {task.blocksShiftClosing && <Chip label="без неё не закрыть смену" kind="block" />}
              {req.photoRequired && <Chip label="нужно фото" />}
              {req.amountRequired && <Chip label="нужна сумма" />}
              {req.commentRequired && <Chip label="нужен комментарий" />}
            </View>

            {req.amountRequired && (
              <Field label="Сумма, ₽" value={amount} onChangeText={setAmount} keyboardType="decimal-pad" placeholder="0" />
            )}
            {req.commentRequired && (
              <Field label="Комментарий" value={comment} onChangeText={setComment} placeholder="Что сделано / что заметили" multiline />
            )}

            {wantsPhoto && (
              <View style={styles.photoBlock}>
                <View style={styles.photoHead}>
                  <Text style={styles.photoLabel}>
                    {photos.length ? `Фото: ${photos.length}${req.photoRequired ? '' : ' (по желанию)'}` : (req.photoRequired ? 'Прикрепите фото' : 'Фото по желанию')}
                  </Text>
                  <TextButton title={photos.length ? 'Добавить ещё' : 'Прикрепить'} onPress={addPhotos} />
                </View>
                {photos.length > 0 && (
                  <View style={styles.thumbs}>
                    {photos.map((p) => (
                      <Pressable key={p.id} style={styles.thumb} onPress={() => removePhoto(p.id)}>
                        <Text style={styles.thumbName} numberOfLines={1}>📷 {p.name}</Text>
                        <Text style={styles.thumbX}>✕</Text>
                      </Pressable>
                    ))}
                  </View>
                )}
              </View>
            )}

            <PrimaryButton title="Отметить выполненной" onPress={submit} loading={busy} disabled={missing.length > 0} />
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  wrap: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'flex-end' },
  sheet: { backgroundColor: colors.surface, borderTopLeftRadius: radius.lg, borderTopRightRadius: radius.lg, padding: spacing.lg, maxHeight: '88%' },
  head: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm, marginBottom: spacing.md },
  grow: { flex: 1 },
  body: { gap: spacing.md },
  title: { ...typography.h3, color: colors.textPrimary },
  meta: { ...typography.caption, color: colors.textMuted, marginTop: 2 },
  desc: { ...typography.body, color: colors.textSecondary },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
  photoBlock: { backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border, borderRadius: radius.sm, padding: spacing.md, gap: spacing.sm },
  photoHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  photoLabel: { ...typography.caption, color: colors.textSecondary, flex: 1, paddingRight: spacing.sm },
  thumbs: { gap: 6 },
  thumb: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border, borderRadius: radius.sm, paddingHorizontal: spacing.sm, paddingVertical: 8 },
  thumbName: { ...typography.caption, color: colors.textPrimary, flex: 1 },
  thumbX: { ...typography.caption, color: colors.danger, fontWeight: '800' },
});
